/* eslint-disable no-nested-ternary */
import { AccountStateEnum } from "../../contexts/AuthContext";
import { IMultiMarkPriceAssetDerivative } from "../../hooks/wss/useMarkPriceWSS";
import { getCurrentChainConfig } from "../../config/chains";
import { getToken } from "../../config/tokens";
import {
  findMatchingSpotCollateral,
  getDepositTokenSymbol,
} from "../../utils/spotCollateral";
import { Button, ButtonThemeEnum } from "../Buttons/styles";
import Orders from "../Orders";
import Positions from "../Positions";
import Collateral from "../PortfolioSettings/Collateral";
import { OrderHistoryTab } from "../../pages/OrderHistoryTab";
import { PositionHistoryTab } from "../../pages/PositionHistoryTab";
import { TradeHistoryTab } from "../../pages/TradeHistoryTab";
import { EmptyContent } from "./style";
import { MarketPortfolioOptionCount, MarketPortfolioOptionEnum } from "./types";

type TranslateFn = (
  key: string,
  options?: { defaultValue?: string }
) => string;

export function computeUniqueTickers(
  allPositionsForMarks: any[]
): IMultiMarkPriceAssetDerivative {
  const tickers: IMultiMarkPriceAssetDerivative = {};
  allPositionsForMarks.forEach((pos) => {
    // If option, check if already subbed
    if (pos.option) {
      const optionTickerExists = Boolean(tickers[pos.instrument_name]);
      if (!optionTickerExists) {
        tickers[pos.instrument_name] = {
          asset: pos.asset,
          derivative: pos.instrument_type,
        };
      }
    } else {
      tickers[pos.instrument_name] = {
        asset: pos.asset,
        derivative: pos.instrument_type,
      };
    }
  });
  return tickers;
}

/**
 * True while there is no usable account to show positions, orders or history
 * for. Shared by the desktop bottom bar and every mobile tab: without it the
 * mobile tabs answered a disconnected visitor with "You have no positions",
 * a claim about an account nobody has connected (QA C-1, 2026-10-01).
 */
export function needsWalletConnect(
  account: string | null | undefined,
  accountApiKeyState: AccountStateEnum,
  showOnboarding: boolean | undefined
): boolean {
  return (
    !account || accountApiKeyState !== AccountStateEnum.OK || !!showOnboarding
  );
}

/**
 * The sentence above the gate's button, matching the button. It always said
 * "Connect your wallet…" -- also to a trader who WAS connected and signed in
 * but had not deposited yet (onboarding), which read as a broken session.
 */
export function walletGateMessage(
  accountApiKeyState: AccountStateEnum,
  showDerivativeUi = true
): string {
  const what = showDerivativeUi
    ? "positions, orders, and trade history"
    : "orders and trade history";
  switch (accountApiKeyState) {
    case AccountStateEnum.OK:
      return "Deposit funds to start trading";
    case AccountStateEnum.REQUIRE_PASSWORD:
      return `Unlock trading to view your ${what}`;
    case AccountStateEnum.REQUIRE_REGISTER_SIGNING:
      return `Complete sign-in to view your ${what}`;
    default:
      return `Connect your wallet to view ${what}`;
  }
}

/** What stands in for positions / orders / history until there is an account. */
export function ConnectWalletEmptyState({
  accountApiKeyState,
  showDerivativeUi = true,
  onConnect,
  t,
}: {
  accountApiKeyState: AccountStateEnum;
  showDerivativeUi?: boolean;
  onConnect: () => void;
  t: TranslateFn;
}) {
  return (
    <EmptyContent $fillHeight data-testid="connect-wallet-empty-state">
      <span style={{ marginBottom: 8, opacity: 0.5 }}>
        {walletGateMessage(accountApiKeyState, showDerivativeUi)}
      </span>
      <Button buttonTheme={ButtonThemeEnum.HIGHLIGHT} onClick={onConnect}>
        {accountApiKeyState === AccountStateEnum.REQUIRE_PASSWORD
          ? t("unlock_trading")
          : accountApiKeyState === AccountStateEnum.OK
          ? t("continue_onboarding")
          : accountApiKeyState === AccountStateEnum.REQUIRE_REGISTER_SIGNING
          ? t("complete_sign_in")
          : t("connect_wallet")}
      </Button>
    </EmptyContent>
  );
}

export function computeMarketPortfolioOptionsCount(
  isAuthenticated: boolean,
  positionsTotalCount: number | undefined,
  accountPositionsLength: number,
  ordersTotalCount: number | undefined,
  ordersDataLength: number
): MarketPortfolioOptionCount {
  // Badge counts come from the backend's `pagination.total_count`, so they
  // reflect the full filtered set — not just the rows loaded so far.
  return {
    Positions: isAuthenticated ? (positionsTotalCount ?? accountPositionsLength) : 0,
    Orders: isAuthenticated ? (ordersTotalCount ?? ordersDataLength) : 0,
    // History tabs own their own cursor-paginated fetches; no badge count.
    "Position History": undefined,
    "Trade History": undefined,
    "Order History": undefined,
    Portfolio: undefined,
  };
}

export function getEmptyContentTextForTab(
  t: TranslateFn,
  tab: MarketPortfolioOptionEnum
) {
  switch (tab) {
    case MarketPortfolioOptionEnum.Positions:
      return t("no_positions");
    case MarketPortfolioOptionEnum.OpenOrders:
      return t("no_orders");
    case MarketPortfolioOptionEnum.Portfolio:
      return t("no_assets");
    default:
      return "";
  }
}

/** Tabs the trade-page bottom bar lists for this build / account. */
export function getVisibleMarketPortfolioTabs(
  showDerivativeUi: boolean
): MarketPortfolioOptionEnum[] {
  return Object.values(MarketPortfolioOptionEnum).filter((tab) => {
    switch (tab) {
      case MarketPortfolioOptionEnum.Positions:
      case MarketPortfolioOptionEnum.PositionHistory:
        return showDerivativeUi;
      case MarketPortfolioOptionEnum.OpenOrders:
      case MarketPortfolioOptionEnum.TradeHistory:
      case MarketPortfolioOptionEnum.OrderHistory:
      case MarketPortfolioOptionEnum.Portfolio:
        return true;
      default: {
        const _exhaustive: never = tab;
        return _exhaustive;
      }
    }
  });
}

export function getTabTitleForTab(
  marketPortfolioOptionsCount: MarketPortfolioOptionCount,
  t: TranslateFn,
  tab: MarketPortfolioOptionEnum
) {
  const count = marketPortfolioOptionsCount[tab];
  switch (tab) {
    case MarketPortfolioOptionEnum.Positions:
      return `${t("positions")} ${count ? `(${count})` : ""}`;
    case MarketPortfolioOptionEnum.OpenOrders:
      return `${t("open_orders")} ${count ? `(${count})` : ""}`;
    case MarketPortfolioOptionEnum.PositionHistory:
      return t("position_history", { defaultValue: "Position History" });
    case MarketPortfolioOptionEnum.TradeHistory:
      return t("trade_history", { defaultValue: "Trade History" });
    case MarketPortfolioOptionEnum.OrderHistory:
      return t("order_history", { defaultValue: "Order History" });
    case MarketPortfolioOptionEnum.Portfolio:
      return t("portfolio");
    default:
      return "";
  }
}

export function computeSpotModalState(
  collaterals: any,
  mode: "deposit" | "withdraw",
  asset: any
) {
  const token = getToken(
    getCurrentChainConfig().chainId,
    getDepositTokenSymbol(String(asset))
  );
  const collat = findMatchingSpotCollateral(
    collaterals,
    String(asset),
    token?.address
  );
  return {
    mode,
    symbol: token?.symbol || String(asset || "MUSDC"),
    address: token?.address || "",
    decimals: token?.decimals || 6,
    available: collat?.available_balance || "0",
  };
}

export interface BottomBarContentParams {
  account: any;
  accountApiKeyState: AccountStateEnum;
  showOnboarding: any;
  setShowConnectModal: (show: boolean) => void;
  t: TranslateFn;
  accountPositions: any[];
  positionsLoadingInitial: boolean;
  ordersData: any[];
  ordersLoadingInitial: boolean;
  getEmptyContentText: (tab: MarketPortfolioOptionEnum) => string;
  currentTab: MarketPortfolioOptionEnum;
  showOnlyCurrentMarket: boolean;
  market: any;
  instrumentMark: any;
  accountData: any;
  positionsHasMore: boolean;
  positionsLoadingMore: boolean;
  loadMorePositions: () => void;
  ordersHasMore: boolean;
  ordersLoadingMore: boolean;
  loadMoreOrders: () => void;
  openDepositFromPortfolio: (asset: any) => void;
  openSpotModal: (mode: "deposit" | "withdraw") => (asset: any) => void;
  openTransferFromPortfolio: (asset: any) => void;
  showDerivativeUi?: boolean;
  /**
   * `/positions` or `/account` failed. With no rows to show, the list is
   * UNKNOWN, not empty: "You currently have no positions" would be a claim the
   * app cannot make.
   */
  positionsError?: unknown;
  onRetryPositions?: () => void;
}

export function computeBottomBarContent(
  params: BottomBarContentParams
): JSX.Element | undefined {
  const {
    account,
    accountApiKeyState,
    showOnboarding,
    setShowConnectModal,
    t,
    accountPositions,
    positionsLoadingInitial,
    ordersData,
    ordersLoadingInitial,
    getEmptyContentText,
    currentTab,
    showOnlyCurrentMarket,
    market,
    instrumentMark,
    accountData,
    positionsHasMore,
    positionsLoadingMore,
    loadMorePositions,
    ordersHasMore,
    ordersLoadingMore,
    loadMoreOrders,
    openDepositFromPortfolio,
    openSpotModal,
    openTransferFromPortfolio,
    showDerivativeUi = true,
    positionsError,
    onRetryPositions,
  } = params;

  if (needsWalletConnect(account, accountApiKeyState, showOnboarding)) {
    return (
      <ConnectWalletEmptyState
        accountApiKeyState={accountApiKeyState}
        showDerivativeUi={showDerivativeUi}
        onConnect={() => setShowConnectModal(true)}
        t={t}
      />
    );
  }

  const positionsLoading = !accountPositions.length && positionsLoadingInitial;
  const ordersLoading = !ordersData.length && ordersLoadingInitial;

  // Default is empty content
  let content: JSX.Element | undefined = (
    <EmptyContent $fillHeight>{getEmptyContentText(currentTab)}</EmptyContent>
  );

  switch (currentTab) {
    case MarketPortfolioOptionEnum.Positions: {
      if (positionsLoading || accountPositions.length) {
        content = (
          <Positions
            selectedMarketType={
              showOnlyCurrentMarket ? market.derivative : undefined
            }
            loading={positionsLoading}
            positions={accountPositions || []}
            instrumentMark={instrumentMark}
            inLiquidation={!!accountData?.in_liquidation}
            hasMore={positionsHasMore}
            isLoadingMore={positionsLoadingMore}
            onLoadMore={loadMorePositions}
          />
        );
      } else if (positionsError) {
        content = (
          <EmptyContent $fillHeight role="alert">
            <span style={{ marginBottom: 8 }}>
              {t("positions_load_failed", {
                defaultValue: "Couldn't load your positions.",
              })}
            </span>
            {onRetryPositions && (
              <Button
                buttonTheme={ButtonThemeEnum.NEUTRAL2}
                onClick={onRetryPositions}
              >
                {t("retry", { defaultValue: "Retry" })}
              </Button>
            )}
          </EmptyContent>
        );
      }
      break;
    }
    case MarketPortfolioOptionEnum.OpenOrders:
      if (ordersLoading || ordersData?.length) {
        content = (
          <Orders
            // Scope by asset only (spot + perp), matching openOrdersMarketFilter.
            // selectedMarketType stays undefined so "cancel all" and columns
            // cover the asset's spot AND perp orders, not just perps.
            selectedAssetType={
              showOnlyCurrentMarket ? market.asset : undefined
            }
            loading={ordersLoading}
            orders={ordersData || []}
            hasMore={ordersHasMore}
            isLoadingMore={ordersLoadingMore}
            onLoadMore={loadMoreOrders}
          />
        );
      }
      break;
    // History tabs list every enabled vertical by design, so scope by asset
    // ONLY — not instrument type. Forcing PERPETUAL here would hide the current asset's
    // spot fills (e.g. on BTC-PERP the user's BTC-USDC spot trades would vanish,
    // showing "no history" when there are no BTC-PERP trades). asset alone lets
    // the backend include both the perp market and spot pools for that asset.
    case MarketPortfolioOptionEnum.PositionHistory:
      content = (
        <PositionHistoryTab
          mode="trading"
          hideTitle
          hideFilter
          assetFilter={showOnlyCurrentMarket ? market.asset : undefined}
        />
      );
      break;
    case MarketPortfolioOptionEnum.TradeHistory:
      content = (
        <TradeHistoryTab
          mode="trading"
          hideTitle
          hideFilter
          assetFilter={showOnlyCurrentMarket ? market.asset : undefined}
        />
      );
      break;
    case MarketPortfolioOptionEnum.OrderHistory:
      content = (
        <OrderHistoryTab
          mode="trading"
          hideTitle
          hideFilter
          assetFilter={showOnlyCurrentMarket ? market.asset : undefined}
        />
      );
      break;
    case MarketPortfolioOptionEnum.Portfolio:
      content = (
        <Collateral
          accountData={accountData}
          onDeposit={openDepositFromPortfolio}
          onWithdraw={openSpotModal("withdraw")}
          onTransfer={openTransferFromPortfolio}
        />
      );
      break;
    default:
      break;
  }

  return content;
}
